import React, { ComponentProps, ReactNode, useMemo } from 'react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Truncated } from '../../../truncated';

type CallBubbleProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  callResult?: 'missed' | 'inbound' | 'outbound' | 'abandoned' | 'voicemail';
  callAction?: () => void;
  playAction?: () => void;
};

export const CallBubble: React.FC<CallBubbleProps> = ({ title, subtitle, callResult, callAction, playAction }) => {
  const iconName: ComponentProps<typeof Icon>['name'] = useMemo(() => {
    switch (callResult) {
      case 'inbound':
        return 'phone-incoming';
      case 'outbound':
        return 'phone-outgoing-small';
      case 'voicemail':
        return 'voicemail';
      case 'abandoned':
      case 'missed':
        return 'phone-missed';
      default:
        return 'phone';
    }
  }, [callResult]);

  const iconBgColor = useMemo(() => {
    switch (callResult) {
      case 'inbound':
        return theme.colors.primary5;
      case 'outbound':
        return theme.colors.neutral5;
      case 'abandoned':
        return '#FFF6D6';
      case 'voicemail':
        return 'rgba(0, 0, 0, 0.1)';
      default:
        return theme.colors.critical5;
    }
  }, [callResult]);

  return (
    <div
      onClick={callAction ? callAction : undefined}
      css={{
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: theme.spacing(1),
        display: 'inline-flex',
        cursor: callAction ? 'pointer' : 'default',
        minWidth: '160px',
        maxWidth: '170px',
      }}
    >
      <div css={{ borderRadius: theme.spacing(1), padding: theme.spacing(0.5), backgroundColor: iconBgColor }}>
        <Icon name={iconName} size={24} />
      </div>
      <div>
        <Truncated
          style={{
            maxWidth: 120,
            fontSize: theme.fontSize(14),
          }}
        >
          {title}
        </Truncated>
        {subtitle && (
          <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
            {callResult === 'inbound' && <Icon name='phone-call' css={{ color: theme.colors.neutral70 }} size={12} />}
            {/* Create a new enum for auto message icon once we support other types of robocall */}
            {callResult === 'outbound' && subtitle === 'Appointment Reminder' && (
              <Icon name='automated-message' css={{ color: theme.colors.neutral70 }} size={12} />
            )}
            <Truncated
              style={{
                maxWidth: 130,
                fontSize: theme.fontSize(12),
                color: theme.font.colors.subdued,
              }}
            >
              {subtitle}
            </Truncated>
          </div>
        )}
      </div>
      {callAction && (
        <div
          css={{
            padding: theme.spacing(0.5),
            borderRadius: theme.spacing(0.5),
            marginLeft: 'auto',
            '&:hover': {
              backgroundColor: theme.colors.neutral20,
            },
          }}
        >
          <Icon
            name='play'
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              (playAction ?? callAction)?.();
            }}
            css={{
              cursor: 'pointer',
            }}
            size={16}
          />
        </div>
      )}
    </div>
  );
};
